import { useEffect, useMemo, useState } from "react";
import { ErrorPanel, Loading } from "./ResourceView";
import { inventoryApi, type TentAssemblyComponent } from "../lib/inventory";
import { useRpcResource } from "../lib/useRpcResource";

const label = (value: string) => value.replaceAll("_", " ");
const shownDimension = (meters: number | null, unit: "m" | "ft" | null) => {
  if (meters == null) return "—";
  const value = unit === "ft" ? meters / 0.3048 : meters;
  return `${Number(value.toFixed(2)).toLocaleString()}${unit === "ft" ? "'" : "m"}`;
};

export function TentAssemblyManager({
  organizationId,
  refreshKey = 0,
}: {
  organizationId: string;
  refreshKey?: number;
}) {
  const resource = useRpcResource(
    () => inventoryApi.tentAssemblies(organizationId),
    [organizationId, refreshKey],
  );
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [partSearch, setPartSearch] = useState("");
  const [partId, setPartId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [role, setRole] = useState("required");
  const [partNotes, setPartNotes] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState("");
  const [lengthM, setLengthM] = useState("");
  const [packageName, setPackageName] = useState("");
  const [assemblyNotes, setAssemblyNotes] = useState("");
  const [showNewAssembly, setShowNewAssembly] = useState(false);

  useEffect(() => {
    if (!resource.data?.assemblies.length) return;
    if (
      !selectedId ||
      !resource.data.assemblies.some((x) => x.id === selectedId)
    )
      setSelectedId(resource.data.assemblies[0].id);
  }, [resource.data, selectedId]);

  const selected =
    resource.data?.assemblies.find((x) => x.id === selectedId) ?? null;
  const availableParts = useMemo(() => {
    const q = partSearch.trim().toLowerCase();
    return (resource.data?.component_products ?? []).filter(
      (p) =>
        p.id !== selected?.product_id &&
        (!q ||
          `${p.sku ?? ""} ${p.name} ${p.category ?? ""}`
            .toLowerCase()
            .includes(q)),
    );
  }, [resource.data, selected?.product_id, partSearch]);

  function clearPart() {
    setPartId("");
    setQuantity("1");
    setRole("required");
    setPartNotes("");
    setEditingId(null);
  }
  function editPart(part: TentAssemblyComponent) {
    setEditingId(part.id);
    setPartId(part.product_id);
    setQuantity(String(part.quantity));
    setRole(part.is_optional ? "optional" : part.component_role);
    setPartNotes(part.notes ?? "");
  }
  async function savePart() {
    if (!selected || !partId || Number(quantity) <= 0) return;
    setBusy(true);
    setMessage(null);
    setActionError(null);
    const result = await inventoryApi.saveTentAssemblyComponent({
      organizationId,
      configurationSizeId: selected.id,
      componentProductId: partId,
      quantity: Number(quantity),
      componentRole: role,
      isOptional: role === "optional",
      notes: partNotes || null,
    });
    setBusy(false);
    if (result.error) {
      setActionError(result.error.message);
      return;
    }
    setMessage(
      editingId ? "Tent part updated." : "Part assigned to the tent assembly.",
    );
    clearPart();
    await resource.reload();
  }
  async function removePart(part: TentAssemblyComponent) {
    if (!window.confirm(`Remove ${part.name} from this tent assembly?`)) return;
    setBusy(true);
    setMessage(null);
    setActionError(null);
    const result = await inventoryApi.removeTentAssemblyComponent(
      organizationId,
      part.id,
    );
    setBusy(false);
    if (result.error) {
      setActionError(result.error.message);
      return;
    }
    if (editingId === part.id) clearPart();
    setMessage("Part removed from the tent assembly.");
    await resource.reload();
  }
  async function saveAssembly() {
    const profile = resource.data?.configured_products.find(
      (x) => x.profile_id === profileId,
    );
    if (!profile || Number(lengthM) <= 0) return;
    setBusy(true);
    setMessage(null);
    setActionError(null);
    const result = await inventoryApi.saveTentAssembly({
      organizationId,
      profileId,
      configuredLengthM: Number(lengthM),
      packageName: packageName || `${profile.name} · ${lengthM}m assembly`,
      notes: assemblyNotes || null,
    });
    setBusy(false);
    if (result.error) {
      setActionError(result.error.message);
      return;
    }
    setProfileId("");
    setLengthM("");
    setPackageName("");
    setAssemblyNotes("");
    setShowNewAssembly(false);
    setMessage("Tent assembly created. Select it and assign its parts.");
    if (result.data) setSelectedId(result.data);
    await resource.reload();
  }

  if (resource.loading && !resource.data)
    return (
      <section className="card tent-assembly-card">
        <Loading />
      </section>
    );
  if (resource.error)
    return (
      <section className="card tent-assembly-card">
        <h2>Tent assemblies / BOM</h2>
        <ErrorPanel message={resource.error} />
      </section>
    );
  if (!resource.data) return null;
  const data = resource.data;
  const chosenProfile = data.configured_products.find(
    (x) => x.profile_id === profileId,
  );

  return (
    <section className="card tent-assembly-card">
      <div className="tent-assembly-heading">
        <div>
          <h2>Tent assemblies / BOM</h2>
          <p className="muted">
            Group individual inventory parts into one rentable tent size. The
            assembly defines what makes the tent; physical counts remain on each
            part.
          </p>
        </div>
        <div className="tent-assembly-heading-actions">
          <span className="status-pill status-active">
            {data.assemblies.length} assemblies
          </span>
          {data.capabilities.manage && (
            <button
              className="primary"
              aria-expanded={showNewAssembly}
              onClick={() => setShowNewAssembly((current) => !current)}
            >
              Create another tent size
            </button>
          )}
        </div>
      </div>
      {message && <div className="success-box">{message}</div>}
      {actionError && <ErrorPanel message={actionError} />}{" "}
      {!data.capabilities.manage && (
        <div className="notice notice-warning">
          Your Inventory role can view assemblies but cannot change them.
        </div>
      )}
      {data.capabilities.manage && showNewAssembly && (
        <div className="tent-new-assembly">
          <div className="tent-new-assembly-heading">
            <strong>Create another tent size</strong>
            <button onClick={() => setShowNewAssembly(false)}>Close</button>
          </div>
          <div className="form-grid four">
            <label className="field">
              <span>Configured tent product</span>
              <select
                value={profileId}
                onChange={(e) => {
                  setProfileId(e.target.value);
                  const p = data.configured_products.find(
                    (x) => x.profile_id === e.target.value,
                  );
                  setLengthM(
                    p?.default_length_m ? String(p.default_length_m) : "",
                  );
                }}
              >
                <option value="">Select tent product</option>
                {data.configured_products.map((p) => (
                  <option key={p.profile_id} value={p.profile_id}>
                    {p.sku ? `${p.sku} · ` : ""}
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Length (meters)</span>
              <input
                type="number"
                min={chosenProfile?.minimum_length_m ?? 0.01}
                max={chosenProfile?.maximum_length_m ?? undefined}
                step={chosenProfile?.length_increment_m ?? 0.01}
                value={lengthM}
                onChange={(e) => setLengthM(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Assembly name</span>
              <input
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder="Example: 15m × 25m structure"
              />
            </label>
            <label className="field">
              <span>Notes</span>
              <input
                value={assemblyNotes}
                onChange={(e) => setAssemblyNotes(e.target.value)}
                placeholder="Package or engineering notes"
              />
            </label>
          </div>
          <button
            className="primary"
            disabled={busy || !profileId || Number(lengthM) <= 0}
            onClick={() => void saveAssembly()}
          >
            Create tent assembly
          </button>
        </div>
      )}
      <div className="tent-assembly-picker">
        <label className="field">
          <span>Big tent / configured size</span>
          <select
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value);
              clearPart();
            }}
          >
            <option value="">Select tent assembly</option>
            {data.assemblies.map((x) => (
              <option key={x.id} value={x.id}>
                {x.sku ? `${x.sku} · ` : ""}
                {x.name} · {shownDimension(x.width_m, x.display_unit)} ×{" "}
                {shownDimension(x.configured_length_m, x.display_unit)} ·{" "}
                {x.component_count} parts
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <div className="tent-assembly-summary">
            <strong>{selected.source_package_name ?? selected.name}</strong>
            <span>
              {shownDimension(selected.width_m, selected.display_unit)} ×{" "}
              {shownDimension(
                selected.configured_length_m,
                selected.display_unit,
              )}{" "}
              · {selected.component_count} component lines
            </span>
            <small>{selected.notes ?? "No assembly notes."}</small>
          </div>
        )}
      </div>
      {selected ? (
        <>
          {data.capabilities.manage && (
            <div className="tent-part-editor">
              <label className="field">
                <span>Find inventory part</span>
                <input
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                  placeholder="Search part name, SKU or category"
                />
              </label>
              <label className="field tent-part-select">
                <span>Part to assign</span>
                <select
                  value={partId}
                  onChange={(e) => setPartId(e.target.value)}
                >
                  <option value="">Select component</option>
                  {availableParts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.sku ? `${p.sku} · ` : ""}
                      {p.name}
                      {p.category ? ` · ${p.category}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Quantity in this tent</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Part role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="required">Required</option>
                  <option value="accessory">Accessory</option>
                  <option value="consumable">Consumable</option>
                  <option value="optional">Optional</option>
                </select>
              </label>
              <label className="field tent-part-notes">
                <span>Notes</span>
                <input
                  value={partNotes}
                  onChange={(e) => setPartNotes(e.target.value)}
                  placeholder="Position, color, connection or packing notes"
                />
              </label>
              <div className="row-actions tent-part-actions">
                <button
                  className="primary"
                  disabled={busy || !partId || Number(quantity) <= 0}
                  onClick={() => void savePart()}
                >
                  {editingId ? "Save part" : "Assign part to tent"}
                </button>
                {editingId && (
                  <button disabled={busy} onClick={clearPart}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="table-wrap tent-assembly-table inventory-scroll-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Part</th>
                  <th>Category</th>
                  <th>Tracking</th>
                  <th>Role</th>
                  <th>Quantity</th>
                  {data.capabilities.manage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {selected.components.map((part) => (
                  <tr key={part.id}>
                    <td>
                      <strong>{part.name}</strong>
                      <small>
                        {part.sku ?? "No SKU"}
                        {part.notes ? ` · ${part.notes}` : ""}
                      </small>
                    </td>
                    <td>{part.category ?? "—"}</td>
                    <td>
                      {label(part.tracking_method)}
                      <small>
                        {part.inventory_tracked
                          ? "Inventory tracked"
                          : "Not inventory tracked"}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`status-pill ${part.is_optional ? "status-warning" : "status-active"}`}
                      >
                        {part.is_optional
                          ? "optional"
                          : label(part.component_role)}
                      </span>
                    </td>
                    <td>
                      <strong>
                        {Number(part.quantity).toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </strong>{" "}
                      {part.unit_of_measure ?? ""}
                    </td>
                    {data.capabilities.manage && (
                      <td>
                        <div className="row-actions">
                          <button
                            disabled={busy}
                            onClick={() => editPart(part)}
                          >
                            Edit
                          </button>
                          <button
                            className="danger"
                            disabled={busy}
                            onClick={() => void removePart(part)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {selected.components.length === 0 && (
              <div className="empty-state">
                No parts are assigned to this tent size yet.
              </div>
            )}
          </div>
          <p className="tent-snapshot-note">
            Changes apply to future Orders. Existing accepted Orders keep the
            BOM snapshot they were sold with.
          </p>
        </>
      ) : (
        <div className="empty-state">
          Create or select a tent assembly to assign component parts.
        </div>
      )}
    </section>
  );
}
